/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";

import { spacing } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";

const hallowElement: HallowElement = "div";

const baseStyle: Interpolation = {
  margin: "0 auto",
  maxWidth: "100vw",
  transformStyle: "preserve-3d",
  width: spacing.full,
};
const minHeightStyle: Interpolation = {
  minHeight: `calc(100vh - ${spacing.xxxl})`,
};
const paddingXStyle: Interpolation = {
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.xxl,
    paddingRight: spacing.xxl,
  },
};
const paddingYStyle: Interpolation = {
  paddingBottom: spacing.l,
  paddingTop: spacing.l,
};

const sizeStyles: Record<ContainerProps["size"], Interpolation> = {
  xs: {
    maxWidth: `min(100vw, ${spacing.maxWidthWithPaddingXS})`,
  },
  s: {
    maxWidth: `min(100vw, ${spacing.maxWidthWithPaddingS})`,
  },
  m: {
    maxWidth: `min(100vw, ${spacing.maxWidthWithPaddingM})`,
  },
  ms: {
    maxWidth: `min(100vw, ${spacing.maxWidthWithPaddingMS})`,
  },
  l: {
    maxWidth: spacing.maxWidthWithPaddingL,
  },
};

export type ContainerProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  maxWidth?: boolean;
  minHeight?: boolean;
  paddingX?: boolean;
  paddingY?: boolean;
  size?: "xs" | "s" | "ms" | "m" | "l";
};

export const Container = ({
  asChild = false,
  children,
  maxWidth = false,
  minHeight = false,
  paddingX = false,
  paddingY = false,
  size = "l",
  ...props
}: ContainerProps) => {
  const DeterminedElement = determineElementFromAsChild({
    asChild,
    hallowElement,
  });

  return (
    <DeterminedElement
      css={[
        baseStyle,
        maxWidth ? sizeStyles[size] : null,
        minHeight ? minHeightStyle : null,
        paddingX ? paddingXStyle : null,
        paddingY ? paddingYStyle : null,
      ]}
      {...props}
    >
      {children}
    </DeterminedElement>
  );
};
